import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Crown, TrendingUp, Percent, Users, Loader2, Banknote, History, Wallet } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import TopEarners from "@/components/TopEarners";

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

export default function Owner() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/owner/earnings").then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const payout = async () => {
    setBusy(true);
    try { const { data } = await api.post("/owner/payout"); toast.success(`Withdrawal of ${money(data.amount_cents)} initiated`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Payout failed"); } finally { setBusy(false); }
  };

  if (user && user.role !== "owner") return <Navigate to="/feed" replace />;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6" data-testid="owner-page">
      <div className="flex items-center gap-3">
        <span className="w-11 h-11 rounded-xl bg-amber-500/15 border border-amber-500/40 flex items-center justify-center"><Crown className="w-6 h-6 text-amber-400" /></span>
        <div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Owner Dashboard</h1>
          <p className="text-zinc-400 text-sm">Platform revenue & your 10% commission</p>
        </div>
      </div>

      {loading ? <Loader2 className="w-6 h-6 animate-spin text-emerald-400 mt-10" /> : (
        <>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="beam-border rounded-2xl p-6 noise relative overflow-hidden">
              <div className="flex items-center gap-2 text-amber-400 text-sm"><Percent className="w-4 h-4" /> Your Commission</div>
              <p className="mt-2 font-display font-black text-4xl" data-testid="owner-commission">{money(data?.commission_cents)}</p>
            </div>
            <div className="nu-card p-6">
              <div className="flex items-center gap-2 text-emerald-400 text-sm"><TrendingUp className="w-4 h-4" /> Gross Volume</div>
              <p className="mt-2 font-display font-black text-3xl" data-testid="owner-gross">{money(data?.gross_volume_cents)}</p>
            </div>
            <div className="nu-card p-6">
              <div className="flex items-center gap-2 text-zinc-400 text-sm"><Users className="w-4 h-4" /> Paid to Artists</div>
              <p className="mt-2 font-display font-black text-3xl">{money(data?.artist_payout_cents)}</p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="nu-card p-4 text-center"><p className="font-mono-nu text-2xl">{data?.transaction_count ?? 0}</p><p className="text-xs text-zinc-500">tip & ticket transactions</p></div>
            <div className="nu-card p-4 text-center"><p className="font-mono-nu text-2xl">{data?.subscriptions ?? 0}</p><p className="text-xs text-zinc-500">Artist Pass subscriptions</p></div>
          </div>

          {/* Withdraw commission */}
          <div className="mt-6 nu-card p-6">
            <h2 className="font-bold text-lg mb-1 flex items-center gap-2"><Wallet className="w-5 h-5 text-amber-400" /> Withdraw Commission</h2>
            <p className="text-zinc-400 text-sm mb-4">One-tap withdraw of your accumulated commission to your bank via Stripe.</p>
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <p className="text-xs text-zinc-500">Available to withdraw</p>
                <p className="font-display font-black text-2xl text-amber-400" data-testid="owner-available">{money(data?.available_cents)}</p>
              </div>
              <button onClick={payout} disabled={busy} data-testid="owner-withdraw-btn"
                className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber-400 hover:bg-amber-300 text-black font-bold text-sm disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Banknote className="w-4 h-4" />} Withdraw to bank
              </button>
              {data?.withdrawn_cents > 0 && <span className="text-xs text-zinc-500">Withdrawn to date: {money(data.withdrawn_cents)}</span>}
            </div>
            {(data?.payouts || []).length > 0 && (
              <div className="mt-5 pt-4 border-t border-white/5">
                <p className="text-sm font-semibold text-zinc-300 mb-2 flex items-center gap-2"><History className="w-4 h-4" /> Payout history</p>
                <div className="space-y-2" data-testid="owner-payout-history">
                  {data.payouts.map((p) => (
                    <div key={p.id} className="flex items-center justify-between py-1.5 text-sm">
                      <span className="text-zinc-400 flex items-center gap-2"><Banknote className="w-4 h-4 text-amber-400" /> {fmtDate(p.created_at)} <span className="text-xs text-zinc-600 capitalize">· {p.status}</span></span>
                      <span className="font-mono-nu">-{money(p.amount_cents)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="mt-6"><TopEarners title="Top Earners This Week" /></div>

          <div className="mt-6 nu-card p-6">
            <h2 className="font-bold text-lg mb-4">Recent transactions</h2>
            {(!data || data.transactions.length === 0) ? <p className="text-zinc-500 text-sm">No transactions yet.</p> : (
              <div className="space-y-2" data-testid="owner-transactions">
                {data.transactions.map((t) => (
                  <div key={t.session_id} className="flex items-center justify-between py-2 border-b border-white/5 text-sm">
                    <span className="capitalize text-zinc-300">{t.type}</span>
                    <span className="font-mono-nu">{money(t.amount_cents)} <span className="text-amber-400">(+{money(t.fee)})</span></span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
